<script setup lang="ts">
import type { TabsListProps } from 'reka-ui'
import type { HTMLAttributes } from 'vue'
import { cn } from '@elveris/ui/lib/utils'
import { TabsList } from 'reka-ui'
import { computed } from 'vue'

const props = defineProps<
  TabsListProps & {
    class?: HTMLAttributes['class']
    variant?: 'default' | 'glass' | 'neu' | 'modern' | 'glow'
    size?: 'sm' | 'md' | 'lg'
  }
>()

const delegatedProps = computed(() => {
  const { class: _, variant: __, size: ___, ...delegated } = props

  return delegated
})

const listClasses = computed(() => {
  // Base ensures the strip can horizontally scroll at every viewport so
  // a long tab list never forces the page to overflow (WCAG 1.4.10).
  // max-w-full clamps to the parent box; overflow-x-auto + scrollbar-hide
  // make the scroll affordance subtle. flex-wrap turns on at tablet+ where
  // the strip can multi-line if needed.
  const base = [
    'inline-flex items-center justify-start gap-1 transition-all duration-200 motion-reduce:transition-none',
    'max-w-full overflow-x-auto scrollbar-hide overscroll-x-contain',
    'tablet:flex-row',
  ]

  const sizeClasses = {
    sm: 'p-0.5 rounded-md',
    md: 'p-1 rounded-lg',
    lg: 'p-2 rounded-xl',
  }

  const variantClasses = {
    default: ['bg-muted/50 backdrop-blur-sm text-muted-foreground border border-border/50 shadow-modern-sm'],
    glass: [
      'glass-frosted neu-theme-raised border-theme-neutral-2/30 bg-gradient-to-br from-theme-neutral-1/60 to-theme-neutral-2/60 text-theme-fg shadow-modern-md',
    ],
    neu: ['neu-theme-raised border border-theme-neutral-3/50 bg-gradient-to-br from-theme-neutral-1 to-theme-neutral-2 text-theme-fg shadow-modern-sm'],
    modern: [
      'neu-theme-raised border border-theme-neutral-3/50 bg-gradient-to-br from-theme-neutral-1 to-theme-neutral-2 text-theme-fg shadow-modern-sm hover-modern-lift',
    ],
    glow: [
      'neu-theme-raised border border-theme-brand/20 bg-gradient-to-br from-theme-neutral-1 to-theme-neutral-2 text-theme-fg shadow-modern-glow animate-pulse-slow',
    ],
  }

  return cn(...base, sizeClasses[props.size || 'md'], ...variantClasses[props.variant || 'default'], props.class)
})
</script>

<template>
  <TabsList v-bind="delegatedProps" :class="listClasses">
    <slot />
  </TabsList>
</template>
